<template>
  <div class="dropdown nav-item">
    <!-- Megafone: novidades/comunicados do sistema (Painel de News do ADM) -->
    <a href="javascript:;" id="ddlNews" data-bs-toggle="dropdown" aria-expanded="false"
       class="btn btn-icon-only nav-link position-relative" title="Novidades do sistema">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 11l18-5v12L3 14v-3z"></path>
        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path>
      </svg>
      <span v-if="naoLidas > 0" class="badge badge-danger counter">{{ naoLidas > 99 ? '99+' : naoLidas }}</span>
    </a>

    <ul class="dropdown-menu dropdown-menu-right" aria-labelledby="ddlNews"
        style="min-width: 340px; max-width: 400px;">
      <li class="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
        <span class="d-flex align-items-center gap-2"><strong>Novidades</strong><AjudaBotao :slug="AJUDA_SLUGS.SISTEMA_NOVIDADES" variante="popup" titulo="Ajuda: novidades" /></span>
        <small class="text-muted">{{ itens.length }} comunicado(s)</small>
      </li>

      <li v-if="!itens.length" class="px-3 py-3 text-center text-muted">Nenhuma novidade no momento.</li>

      <li v-for="n in itens" :key="n.id">
        <a href="javascript:;" class="dropdown-item d-flex flex-column align-items-start py-2"
           :class="{ 'bg-light': !n.lido }" @click.stop="abreDetalhe(n)">
          <div class="d-flex w-100 justify-content-between align-items-center">
            <span class="badge" :class="badgeTipo(n.tipo)">{{ nomeTipo(n.tipo) }}</span>
            <small class="text-muted ms-2">{{ formatData(n.datapub) }}</small>
          </div>
          <span class="fw-bold text-wrap mt-1">{{ n.titulo }}</span>
        </a>
      </li>
    </ul>

    <!-- Modal de leitura (também usado como popup automático dos destaques)
         IMPORTANTE: precisa ficar no <body>. O header (.header-container) é fixed com
         z-index 1030 e cria contexto de empilhamento; se o modal ficar dentro dele,
         o .modal-backdrop (z-index 1050, anexado ao body) cobre o modal e a tela trava. -->
    <Teleport to="body">
      <div class="modal fade" id="modalNewsDetalhe" ref="modalEl" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable news-modal">
          <div class="modal-content" v-if="newsAberta">
            <div class="modal-header py-2">
              <h6 class="modal-title d-flex align-items-center flex-wrap gap-2 mb-0">
                <span class="badge" :class="badgeTipo(newsAberta.tipo)">{{ nomeTipo(newsAberta.tipo) }}</span>
                <span class="text-wrap">{{ newsAberta.titulo }}</span>
              </h6>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
            </div>

            <div class="modal-body news-corpo" v-html="newsAberta.corpo"></div>

            <div class="modal-footer py-2 d-flex justify-content-between">
              <small class="text-muted">Publicado em {{ formatData(newsAberta.datapub) }}</small>
              <div class="d-flex gap-2">
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="arquivar(newsAberta)">
                  Arquivar
                </button>
                <button type="button" class="btn btn-sm btn-primary" data-bs-dismiss="modal">Fechar</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import AjudaBotao from '@/components/AjudaBotao.vue';
import { AJUDA_SLUGS } from '@/config/ajudaEngage';
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import * as bootstrap from 'bootstrap';
import { listaNews, marcarNewsLida, arquivarNews } from '@/stores/newsStore';

const itens = ref([]);
const newsAberta = ref(null);
const modalEl = ref(null);
let modalInstancia = null;
let pollInterval = null;
let popupJaExibido = false; // popup de destaque abre 1x por sessão de tela

const naoLidas = computed(() => itens.value.filter(n => !n.lido).length);

const tipos = {
  novidade: { nome: 'Novidade', classe: 'badge-primary' },
  aviso: { nome: 'Aviso', classe: 'badge-warning' },
  manutencao: { nome: 'Manutenção', classe: 'badge-danger' },
  atualizacao: { nome: 'Atualização', classe: 'badge-success' },
};
const nomeTipo = (t) => tipos[t]?.nome || 'Comunicado';
const badgeTipo = (t) => tipos[t]?.classe || 'badge-secondary';

const formatData = (dt) => {
  if (!dt) return '';
  try {
    return new Date(dt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
};

const carregar = async () => {
  const json = await listaNews();
  if (!json.ok) return;
  itens.value = json.data || [];

  // Destaque não lido → popup automático (o mais recente; uma vez por carga)
  if (!popupJaExibido) {
    const destaque = itens.value.find(n => n.destaque && !n.lido);
    if (destaque) {
      popupJaExibido = true;
      abreDetalhe(destaque);
    }
  }
};

// Instância única do modal (criada sob demanda, já com o elemento teleportado no body)
const getModal = () => {
  const el = modalEl.value;
  if (!el) return null;
  if (!modalInstancia) {
    modalInstancia = bootstrap.Modal.getInstance(el) || new bootstrap.Modal(el);
  }
  return modalInstancia;
};

const abreDetalhe = async (n) => {
  newsAberta.value = n;

  // Fecha o dropdown do megafone antes de abrir o modal (o clique no item tem .stop)
  const ddl = document.getElementById('ddlNews');
  if (ddl) bootstrap.Dropdown.getInstance(ddl)?.hide();

  // Espera o v-if do modal-content renderizar antes de mostrar
  await nextTick();
  getModal()?.show();

  // Abrir para leitura já marca como lida
  if (!n.lido) {
    n.lido = true;
    await marcarNewsLida(n.id);
  }
};

const arquivar = async (n) => {
  const json = await arquivarNews(n.id);
  if (json.ok) {
    itens.value = itens.value.filter(i => i.id !== n.id);
    getModal()?.hide();
  }
};

onMounted(() => {
  carregar();
  // Novos comunicados chegam sem precisar de F5 (mesmo ritmo do sino)
  pollInterval = setInterval(carregar, 300_000);
});

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval);
  // Descarta o modal: evita backdrop órfão travando a tela se o header for desmontado
  modalInstancia?.dispose();
  modalInstancia = null;
});
</script>

<style scoped>
.news-modal {
  max-width: 640px;
}

/* O scroll fica a cargo do modal-dialog-scrollable; aqui só o conforto de leitura */
.news-corpo {
  word-break: break-word;
}

.news-corpo :deep(img) {
  max-width: 100%;
  height: auto;
}

.news-corpo :deep(table) {
  max-width: 100%;
}

.news-corpo :deep(p:last-child) {
  margin-bottom: 0;
}
</style>
